/**
 * Tenant Utilities
 * Utilities for managing multi-tenant subdomain isolation
 * Ensures proper tenant context isolation across different subdomains
 */

import {
  STAGING_BASE_DOMAIN,
  STAGING_INFRA_LABELS,
  extractOrgSlugFromStagingHostname,
  isStagingApexHostname,
  isStagingDeployHostname,
} from '@/utils/staging-domain.util'
import { tokenManager } from '@/utils/tokenManager'
import Cookies from 'js-cookie'

/**
 * Extract tenant identifier from current hostname
 * Examples:
 * - "qubicgen.vidhyantra.com" -> "qubicgen"
 * - "abc.vidhyantra.com" -> "abc"
 * - "platform.platformsettings.view" -> "platform"
 * - "orgslug.localhost" -> "orgslug"
 * - "localhost" -> "dev"
 * - "vidhyantra.com" (root domain) -> "platform"
 */
export function getTenantFromHostname(): string {
  if (typeof window === 'undefined') {
    return 'platform' // Default for SSR
  }

  const hostname = window.location.hostname

  if (isStagingApexHostname(hostname)) {
    return 'platform'
  }

  const stagingOrgSlug = extractOrgSlugFromStagingHostname(hostname)
  if (stagingOrgSlug) {
    return stagingOrgSlug
  }

  if (isStagingDeployHostname(hostname)) {
    const label = hostname.split('.')[0]
    if (label === 'platform' || (STAGING_INFRA_LABELS as readonly string[]).includes(label)) {
      return 'platform'
    }
  }

  const parts = hostname.split('.')

  // Handle localhost with subdomain (e.g., "orgslug.localhost")
  if (parts.length > 1 && (parts[parts.length - 1] === 'localhost' || parts[parts.length - 1] === '127.0.0.1')) {
    const subdomain = parts[0]
    // Special case: platform subdomain on localhost
    if (subdomain === 'platform') {
      return 'platform'
    }
    return subdomain
  }

  // Handle plain localhost (no subdomain)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'dev' // Use 'dev' instead of 'localhost' for consistency
  }

  // Handle root domain (e.g., "vidhyantra.com" with no subdomain)
  // If only 2 parts (domain + TLD), it's root domain → return 'platform'
  if (parts.length <= 2) {
    return 'platform'
  }

  // Extract subdomain (first part before first dot)
  // platform.vidhyantra.com → platform
  // qubicgen.vidhyantra.com → qubicgen
  if (parts.length > 2) {
    const subdomain = parts[0]
    // Special case: platform subdomain
    if (subdomain === 'platform') {
      return 'platform'
    }
    return subdomain
  }

  return 'platform' // Fallback
}

/**
 * Get base domain (without subdomain)
 * Examples:
 * - "qubicgen.vidhyantra.com" -> "vidhyantra.com"
 * - "abc.staging.vidhyantra.com" -> "staging.vidhyantra.com"
 * - "orgslug.localhost" -> "localhost"
 * - "localhost" -> "localhost"
 */
export function getBaseDomain(): string {
  if (typeof window === 'undefined') {
    return ''
  }

  const hostname = window.location.hostname

  if (isStagingDeployHostname(hostname)) {
    return STAGING_BASE_DOMAIN
  }

  const parts = hostname.split('.')

  // Handle localhost with subdomain (e.g., "orgslug.localhost")
  if (parts.length > 1 && (parts[parts.length - 1] === 'localhost' || parts[parts.length - 1] === '127.0.0.1')) {
    return parts[parts.length - 1] // Return "localhost" or "127.0.0.1"
  }

  // Handle plain localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return hostname
  }

  // Remove subdomain (first part)
  if (parts.length > 1) {
    return parts.slice(1).join('.')
  }

  return hostname
}

/**
 * Build tenant-specific subdomain URL
 * @param tenant - Tenant identifier (orgSlug, branchSlug, or 'platform')
 * @param path - Path to append (default: '/')
 * @param protocol - Protocol to use (default: current protocol)
 */
export function buildTenantUrl(
  tenant: string,
  path: string = '/',
  protocol?: string
): string {
  if (typeof window === 'undefined') {
    return path
  }

  const baseDomain = getBaseDomain()
  const currentProtocol = protocol || window.location.protocol
  const port = window.location.port ? `:${window.location.port}` : ''
  const currentHostname = window.location.hostname
  const envBaseDomain = import.meta.env.VITE_BASE_DOMAIN

  // Handle localhost - build subdomain URL (e.g., orgslug.localhost:3000)
  if (baseDomain === 'localhost' || baseDomain === '127.0.0.1') {
    // Special case: platform tenant on localhost should use plain localhost
    if (tenant === 'platform' || tenant === 'dev') {
      return `${currentProtocol}//${baseDomain}${port}${path}`
    }
    // Build subdomain URL for other tenants on localhost
    const hostname = `${tenant}.${baseDomain}`
    return `${currentProtocol}//${hostname}${port}${path}`
  }

  // Handle vidhyantra.local domain (development with custom domain)
  // If baseDomain is 'local' (from platform.local), check if we should use vidhyantra.local
  // This handles cases like platform.local -> should become platform.vidhyantra.local
  if (baseDomain === 'local' || currentHostname.includes('vidhyantra.local')) {
    const effectiveBaseDomain = 
      (envBaseDomain?.includes('vidhyantra.local') ? envBaseDomain : null) ||
      (currentHostname.includes('vidhyantra.local') ? 'vidhyantra.local' : null) ||
      'vidhyantra.local'

    const hostname = `${tenant}.${effectiveBaseDomain}`
    return `${currentProtocol}//${hostname}${port}${path}`
  }

  // Build subdomain URL for production domains
  const hostname = `${tenant}.${baseDomain}`
  return `${currentProtocol}//${hostname}${port}${path}`
}

/**
 * Check if current URL is on a different tenant than expected
 * @param expectedTenant - The tenant we expect to be on
 */
export function isWrongTenant(expectedTenant: string): boolean {
  const currentTenant = getTenantFromHostname()
  return currentTenant !== expectedTenant
}

/**
 * Redirect to correct tenant subdomain
 * @param tenant - Target tenant identifier
 * @param path - Path to navigate to (default: '/dashboard')
 */
export function redirectToTenant(
  tenant: string, 
  path: string = '/dashboard'
): void {
  const targetUrl = buildTenantUrl(tenant, path)
  // Use window.location.replace for hard redirect (no back button)
  // Cookies are automatically sent by browser to all subdomains
  window.location.replace(targetUrl)
}

/**
 * Get tenant-specific storage key
 * Prefixes keys with tenant identifier to prevent cross-tenant contamination
 * @param key - Base storage key
 * @param tenant - Tenant identifier (default: current tenant)
 */
export function getTenantStorageKey(key: string, tenant?: string): string {
  const tenantId = tenant || getTenantFromHostname()
  return `${tenantId}:${key}`
}

/**
 * Clear all storage for a specific tenant
 * @param tenant - Tenant identifier (default: current tenant)
 */
export function clearTenantStorage(tenant?: string): void {
  const tenantId = tenant || getTenantFromHostname()
  const prefix = `${tenantId}:`

  // Clear localStorage
  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))
  } catch (error) {
    // Silent error handling
  }

  // Clear sessionStorage
  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key))
  } catch (error) {
    // Silent error handling
  }
}

/**
 * Determine tenant from user context
 * @param contextType - Context type (PLATFORM, ORGANIZATION, BRANCH, SELF, ALUMNI)
 * @param orgSlug - Organization slug
 * @param branchSlug - Branch slug (optional)
 */
export function getTenantFromContext(
  contextType: 'PLATFORM' | 'ORGANIZATION' | 'BRANCH' | 'SELF' | 'ALUMNI',
  orgSlug?: string,
  _branchSlug?: string // Unused but kept for API compatibility
): string {
  switch (contextType) {
    case 'PLATFORM':
      return 'platform'
    case 'ORGANIZATION':
    case 'SELF':
    case 'ALUMNI':
    case 'BRANCH':
      // For all organization-scoped contexts (including BRANCH), use only orgSlug
      // Branch slug should be in the URL path, not in the subdomain
      // Example: qubicgen.localhost:3000/orgs/qubicgen/branches/secondbranch
      return orgSlug || 'platform'
    default:
      return 'platform'
  }
}

/**
 * Check if we need to redirect to a different tenant after login
 * @param contextType - Context type from login response
 * @param orgSlug - Organization slug from login response
 * @param branchSlug - Branch slug from login response
 */
export function shouldRedirectToTenant(
  contextType: 'PLATFORM' | 'ORGANIZATION' | 'BRANCH' | 'SELF' | 'ALUMNI',
  orgSlug?: string,
  branchSlug?: string
): boolean {
  const expectedTenant = getTenantFromContext(contextType, orgSlug, branchSlug)
  const currentTenant = getTenantFromHostname()
  
  // Don't redirect if already on correct tenant
  if (currentTenant === expectedTenant) {
    return false
  }

  // Handle localhost: allow redirects to create subdomain URLs (e.g., orgslug.localhost:3000)
  const baseDomain = getBaseDomain()
  if (baseDomain === 'localhost' || baseDomain === '127.0.0.1') {
    // On localhost, 'dev' and 'platform' are equivalent (both represent platform)
    // So don't redirect if switching between them
    if ((currentTenant === 'dev' && expectedTenant === 'platform') ||
        (currentTenant === 'platform' && expectedTenant === 'dev')) {
      return false
    }
    // For any other tenant change on localhost, redirect to create subdomain URL
    return true
  }

  // For production domains, always redirect if tenant is different
  return true
}

/**
 * Cross-domain token migration utility
 * When user manually changes domain but context matches subdomain,
 * this function migrates tokens from storage to URL and navigates
 * 
 * Flow:
 * 1. Check if context matches expected tenant for current subdomain
 * 2. If matching, fetch tokens from storage for that tenant
 * 3. Build URL with tokens as query parameters
 * 4. Clear all storage (localStorage and cookies)
 * 5. Navigate to new URL
 * 6. AuthInitializer on new page will extract tokens from URL and store them
 * 
 * @param context - User context to check against
 * @param currentPath - Current path to preserve during migration
 * @returns true if migration was performed, false otherwise
 */
export function migrateTokensForMatchingContext(
  context: { type: 'PLATFORM' | 'ORGANIZATION' | 'BRANCH' | 'SELF' | 'ALUMNI'; orgSlug?: string; branchSlug?: string } | null,
  _currentPath: string = window.location.pathname
): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  // Skip on localhost (development - no need for migration)
  if (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1') || window.location.hostname.includes('localhost')) {
    return false
  }

  // Need context to determine expected tenant
  if (!context) {
    return false
  }

  // Determine expected tenant from context
  const expectedTenant = getTenantFromContext(
    context.type,
    context.orgSlug,
    context.branchSlug
  )

  // Get current tenant from hostname
  const currentTenant = getTenantFromHostname()

  // Only migrate if context matches current subdomain
  // This means user manually changed domain but subdomain is correct
  if (expectedTenant !== currentTenant) {
    return false // Context doesn't match, don't migrate
  }

  // Cookies work automatically across subdomains - no URL token checking needed

  // Fetch tokens for the expected tenant
  const tokens = tokenManager.getTokensForTenant(expectedTenant)
  
  if (!tokens || !tokens.contextToken) {
    return false // No tokens found for this tenant
  }
  // Cookies work automatically across subdomains - no need to pass tokens in URL
  const targetUrl = window.location.href

  // Clear ALL storage before navigation
  // Clear all tenant-specific storage
  tokenManager.clearAllAuthData()
  
  // Clear all context-related localStorage
  try {
    const contextKeys = ['currentContext', 'contexts', 'currentOrgSlug', 'currentBranchSlug', 
                        'currentOrganizationName', 'currentBranchName', 'user', 'context']
    contextKeys.forEach(key => {
      localStorage.removeItem(key)
      // Also clear for all possible tenants
      for (let i = 0; i < localStorage.length; i++) {
        const storedKey = localStorage.key(i)
        if (storedKey && (storedKey === key || storedKey.includes(`:${key}`))) {
          localStorage.removeItem(storedKey)
        }
      }
    })
  } catch (error) {
  }

  // Clear all cookies
  try {
    const cookieKeys = ['accessToken', 'refreshToken', 'contextToken', 'uiPermissions', 'user', 'context', 'userContext']
    cookieKeys.forEach(key => {
      Cookies.remove(key)
      Cookies.remove(key, { path: '/' })
      if (window.location.hostname) {
        const hostname = window.location.hostname
        Cookies.remove(key, { path: '/', domain: hostname })
        Cookies.remove(key, { path: '/', domain: `.${hostname}` })
      }
    })
  } catch (error) {
  }

  // Clear sidebar menu cache
  // Note: We can't import navigationService here due to circular dependency risk
  // The sidebar menu will be cleared when tokens are cleared, or will be refreshed on new domain
  try {
    // Clear any sidebar-related localStorage keys
    const sidebarKeys = ['sidebarMenu', 'menu', 'navigation']
    sidebarKeys.forEach(key => {
      localStorage.removeItem(key)
      // Also clear tenant-specific keys
      for (let i = 0; i < localStorage.length; i++) {
        const storedKey = localStorage.key(i)
        if (storedKey && storedKey.toLowerCase().includes(key.toLowerCase())) {
          localStorage.removeItem(storedKey)
        }
      }
    })
  } catch (error) {
    // Silent error handling
  }

  // Navigate to new URL with tokens
  window.location.href = targetUrl.toString()

  return true // Migration initiated
}

/**
 * Check if an organization exists and is valid
 * Calls the backend validation endpoint
 * @param slug - Organization slug to validate
 * @returns Validation result
 */
export async function checkOrganizationExists(slug: string): Promise<{
  success: boolean;
  exists: boolean;
  valid: boolean;
  slug?: string;
  name?: string;
  message?: string;
}> {
  try {
    // Dynamic import avoids circular dependency: lib/axios → tokenManager → tenant.util
    const { default: api } = await import('@/lib/axios')
    const { data } = await api.get(`/auth/validate-organization/${encodeURIComponent(slug)}`)
    return data
  } catch {
    return {
      success: false,
      exists: false,
      valid: false,
      message: 'Error validating organization',
    }
  }
}
